class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, openDrawer) {
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      openDrawer == false ? "" : this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// "Complete your set" upsell cards (snippets/cart-upsell-card.liquid). This lives here,
// in an always-loaded <script src> file, rather than as an inline <script> in the snippet:
// the upsells block only renders once the cart is non-empty, and CartDrawer.renderContents()
// replaces #CartDrawer via innerHTML — which never executes embedded <script> tags. An inline
// script would therefore never run the first time the block appears (e.g. adding the very
// first item to an empty cart), leaving its "+" buttons dead until a full page reload.
if (!window.__cuUpsellCardInit) {
  window.__cuUpsellCardInit = true;

  const cuIsVariantInCart = (card, variantId) => {
    const inCart = card.dataset.cuInCartVariants || '';
    return inCart.indexOf(`${variantId},`) !== -1;
  };

  const cuSetAddedState = (addBtn, added) => {
    addBtn.classList.toggle('is-added', added);
    const productTitle = addBtn.getAttribute('aria-label').replace(/^(Added |Add )/, '');
    addBtn.setAttribute('aria-label', `${added ? 'Added' : 'Add'} ${productTitle}`);
  };

  const cuActivateSwatch = (card, variantId, color) => {
    card.querySelectorAll('.cu-card__img[data-color]').forEach((img) => {
      img.classList.toggle('cu-card__img--active', img.dataset.color === color);
    });
    card.querySelectorAll('.cu-swatch[data-variant-id]').forEach((s) => {
      s.classList.toggle('cu-swatch--active', s.dataset.variantId === variantId);
    });
    const addBtn = card.querySelector('[data-cu-add]');
    if (addBtn) {
      addBtn.dataset.variantId = variantId;
      const swatch = card.querySelector(`.cu-swatch[data-variant-id="${variantId}"]`);
      addBtn.disabled = swatch ? swatch.dataset.available === 'false' : false;
      cuSetAddedState(addBtn, cuIsVariantInCart(card, variantId));
    }
  };

  document.addEventListener('click', (e) => {
    const swatch = e.target.closest('.cu-swatch[data-variant-id]');
    if (swatch) {
      const card = swatch.closest('[data-cu-card]');
      if (card) cuActivateSwatch(card, swatch.dataset.variantId, swatch.dataset.color);
      return;
    }

    const addBtn = e.target.closest('[data-cu-add]');
    if (addBtn) {
      if (addBtn.disabled) return;
      const card = addBtn.closest('[data-cu-card]');
      const errorEl = card ? card.querySelector('[data-cu-error]') : null;
      const cartDrawer = document.querySelector('cart-drawer');

      const spinner = addBtn.querySelector('.loading__spinner');
      addBtn.classList.add('loading');
      addBtn.setAttribute('aria-disabled', true);
      spinner?.classList.remove('hidden');
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      const body = { id: addBtn.dataset.variantId, quantity: 1 };
      if (cartDrawer) {
        body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }
      config.body = JSON.stringify(body);

      fetch(routes.cart_add_url, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            if (errorEl) {
              errorEl.textContent = response.description || response.message || 'Could not add to cart.';
              errorEl.hidden = false;
            }
            return;
          }
          if (card) {
            card.dataset.cuInCartVariants = `${card.dataset.cuInCartVariants || ''}${response.variant_id},`;
          }
          cuSetAddedState(addBtn, true);
          if (cartDrawer) cartDrawer.renderContents(response);
        })
        .catch(() => {
          if (errorEl) {
            errorEl.textContent = 'Something went wrong. Please try again.';
            errorEl.hidden = false;
          }
        })
        .finally(() => {
          addBtn.classList.remove('loading');
          addBtn.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
        });
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const swatch = e.target.closest('.cu-swatch[data-variant-id]');
    if (!swatch) return;
    e.preventDefault();
    const card = swatch.closest('[data-cu-card]');
    if (card) cuActivateSwatch(card, swatch.dataset.variantId, swatch.dataset.color);
  });
}
